import classNames from 'classnames';
import React, { FC, useRef, ReactNode, CSSProperties, ChangeEvent, FocusEvent } from 'react';
import styled, { Interpolation } from 'styled-components';

import { Tooltip } from '../tooltip';

interface InputProps {
    name: string;
    value?: string | number;
    type?: string;
    label?: string;
    rightElement?: ReactNode;
    placeholder?: string;
    error?: string;
    separator?: boolean;
    disabled?: boolean;
    hasTooltip?: boolean;
    fullWidth?: boolean;
    hasInput?: boolean;
    className?: string;
    containerStyles?: Interpolation<CSSProperties>;
    // eslint-disable-next-line no-unused-vars
    onChange?: (value: string | number, name: string) => void;
    // eslint-disable-next-line no-unused-vars
    onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
}

interface FieldProps {
    separator?: boolean;
    label?: string;
    placeholder?: string | ReactNode;
    hasInput: boolean;
}

const InputContainer = styled.div`
    margin-bottom: 15px;
    .input-element {
        margin-bottom: 0px;
    }
    &.input-container-fullWidth {
        width: 100%;
    }
`;

const InputElement = styled.div<{
    styles: Interpolation<CSSProperties>;
}>`
    ${({ theme: { colors } }) => `
        display: flex;
        align-items: center;
        justify-content: center;
        border: 1px solid ${colors.border.light};
        border-radius: 4px;
        padding: 0px 10px;
        margin-bottom: 15px;
        input::-webkit-outer-spin-button,
        input::-webkit-inner-spin-button {
            -webkit-appearance: none;
            margin: 0; /* <-- Apparently some margin are still there even though it's hidden */
        }
        input[type='number'] {
            -moz-appearance: textfield; /* Firefox */
        }
        &:hover, :focus-within {
            border-color: ${colors.primary};
        }
        &.input-element-error {
            border-color: ${colors.alert.red};
        }
        &.input-element-fullWidth {
            width: 100%;
        }
    `};
    ${({ styles }) => styles};
`;

const StyledInput = styled.input<FieldProps>`
    ${({ theme: { colors, typography }, hasInput, disabled }) => `
        background: none;
        border: none;
        color: ${colors.text.primary};
        cursor: ${disabled ? 'not-allowed !important' : 'initial'};
        font-size: ${typography.size.desktop.paragraph.p3};
        font-family:  inherit;
        outline: none;
        width: ${hasInput ? '100%' : '0'};
        letter-spacing: 0.05em;
        padding: 12.5px 4px;
        &.input-labeled {
            text-align: right;
        }
        &.input-separator {
            border-right: 1px solid ${colors.border.light};
        }
        
        * {
         cursor: ${disabled ? 'not-allowed !important' : 'initial'};
        }
    `}
`;

const StyledLabel = styled.span<{ htmlFor: string }>`
    ${({ theme: { colors, typography } }) => `
        color: ${colors.text.tertiary};
        font-size: ${typography.size.desktop.paragraph.p3};
        width: 100%;
        width: -moz-available; /* WebKit-based browsers will ignore this. */
        width: -webkit-fill-available; /* Mozilla-based browsers will ignore this. */
        width: fill-available;
        max-width: fit-content;
    `};
`;

const StyledError = styled.span`
    ${({ theme: { colors, typography } }) => `
        color: ${colors.alert.red};
        font-size: ${typography.size.desktop.paragraph.p3};
        line-height: calc(${typography.lineHeight[2]} - 1px);
        width: 100%;
        width: -moz-available; /* WebKit-based browsers will ignore this. */
        width: -webkit-fill-available; /* Mozilla-based browsers will ignore this. */
        width: fill-available;
    `};
`;

const Input: FC<InputProps> = ({
    name,
    value,
    type,
    label,
    placeholder,
    separator = false,
    rightElement,
    containerStyles,
    error,
    disabled,
    hasTooltip = true,
    fullWidth,
    hasInput = true,
    className,
    onChange,
    onBlur,
}) => {
    const fieldRef = useRef<HTMLInputElement>(null);

    // focus on input field
    const focusField = () => {
        fieldRef && fieldRef.current && fieldRef.current.focus();
    };

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        onChange && onChange(e.target.value, e.target.name);
    };

    return hasTooltip ? (
        <Tooltip
            // eslint-disable-next-line no-prototype-builtins
            visible={error ? true : false}
            overlay={error}
        >
            <InputElement
                className={classNames(
                    'input-element',
                    error && 'input-element-error',
                    fullWidth && 'input-element-fullWidth',
                    disabled && 'clickable-disabled',
                    className,
                )}
                styles={containerStyles}
            >
                {label && (
                    <StyledLabel className={'input-label'} onClick={focusField} htmlFor={name}>
                        {label}
                    </StyledLabel>
                )}

                <StyledInput
                    className={classNames(
                        'input',
                        separator && 'input-separator',
                        label && 'input-labeled',
                    )}
                    placeholder={placeholder}
                    value={value}
                    ref={fieldRef}
                    name={name}
                    autoComplete="off"
                    disabled={disabled}
                    hasInput={hasInput}
                    type={type}
                    id={name}
                    onBlur={onBlur}
                    onChange={handleChange}
                />
                {rightElement && rightElement}
            </InputElement>
        </Tooltip>
    ) : (
        <InputContainer
            className={classNames('input-container', fullWidth && 'input-container-fullWidth')}
        >
            <InputElement
                className={classNames('input-element', error && 'input-element-error', className)}
                styles={containerStyles}
            >
                {label && (
                    <StyledLabel className={'input-label'} onClick={focusField} htmlFor={name}>
                        {label}
                    </StyledLabel>
                )}
                <StyledInput
                    className={classNames(
                        'input',
                        separator && 'input-separator',
                        label && 'input-labeled',
                    )}
                    placeholder={placeholder}
                    value={value}
                    ref={fieldRef}
                    name={name}
                    disabled={disabled}
                    hasInput={hasInput}
                    type={type}
                    id={name}
                    onBlur={onBlur}
                    onChange={handleChange}
                />
                {rightElement && rightElement}
            </InputElement>
            {error && <StyledError>{error}</StyledError>}
        </InputContainer>
    );
};

export { Input };
